import { computed, inject, Injectable, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { AccessRightsService } from '../app/services/access-rights.service';
import { environment } from '../environments/environment';
import { Notification } from '../shared/models/notifications/notification';

@Injectable({
  providedIn: 'root',
})
export abstract class NotificationBaseService {
  public readonly receivedNotification = computed(() => this.receivedNotificationSignal());
  protected hubConnection?: signalR.HubConnection;

  protected currentUser?: string;
  protected readonly receivedNotificationSignal = signal<Notification[]>([]);
  private readonly environment = environment;

  private readonly accessService = inject(AccessRightsService);

  constructor(connectionRoute: string) {
    this.accessService.getCurrentUser$().subscribe((user) => {
      this.currentUser = user;
      if (user) {
        this.startConnection(connectionRoute);
      } else {
        this.stopConnection();
      }
    });
  }

  protected startConnection(route: string): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.environment.notificationsUrl}${route}`)
      .withAutomaticReconnect()
      .build();

    this.hubConnection
      .start()
      .then(() => console.log('Connection started'))
      .catch((err) => console.log('Error while starting connection: ' + err));

    this.subscribeToNotifications();
  }

  protected stopConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop().then(() => console.log('Connection stopped'));
    }
  }

  protected addToLocalStorage<T extends Notification>(key: string, notification: T): void {
    const existingNotifications = localStorage.getItem(key);
    const notifications: T[] = existingNotifications ? JSON.parse(existingNotifications) : [];
    notifications.push(notification);
    localStorage.setItem(key, JSON.stringify(notifications));
  }

  protected getFromLocalStorage<T extends Notification>(key: string): T[] {
    const existingNotifications = localStorage.getItem(key);
    return existingNotifications ? JSON.parse(existingNotifications) : [];
  }

  protected removeFromLocalStorage(key: string, notificationId: string): void {
    const existingNotifications = localStorage.getItem(key);
    if (existingNotifications) {
      const notifications: Notification[] = JSON.parse(existingNotifications);
      const updatedNotifications = notifications.filter(
        (notification) => notification.notificationId !== notificationId
      );
      localStorage.setItem(key, JSON.stringify(updatedNotifications));
    }
  }

  protected abstract subscribeToNotifications(): void;
}
